import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

interface CarouselProps {
  children: ReactNode;
  className?: string;
  showNavigation?: boolean;
}

export const Carousel = ({
  children,
  className = '',
  showNavigation = true,
}: CarouselProps) => {
  const ref = useRef<HTMLDivElement>(null!);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  const next = useCallback(() => {
    if (ref.current) {
      const nextPosition = ref.current.scrollLeft + 200;
      ref.current.scrollTo({ top: 0, left: nextPosition, behavior: 'smooth' });
    }
  }, []);

  const prev = useCallback(() => {
    if (ref.current) {
      const prevPosition = ref.current.scrollLeft - 200;
      ref.current.scrollTo({ top: 0, left: prevPosition, behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  return (
    <div className={`relative ${className}`}>
      {/* Navigation Buttons */}
      {showNavigation && (
        <>
          {/* Previous Button */}
          {canScrollLeft && (
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-colors hover:bg-neutral-50"
              aria-label="Previous items"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              <Icon name="ChevronLeft" />
            </button>
          )}

          {/* Next Button */}
          {canScrollRight && (
            <button
              onClick={next}
              className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center transition-colors hover:bg-neutral-50"
              aria-label="Next items"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              <Icon name="ChevronRight" />
            </button>
          )}
        </>
      )}

      {/* Scrollable Container */}
      <div
        ref={ref}
        className="relative overflow-x-auto"
        onScroll={handleScroll}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <div className="mx-auto flex w-max min-w-full justify-center gap-4">
          {children}
        </div>
      </div>
    </div>
  );
};
